import { AxiosResponse } from 'axios';

import AbstractSiteApi from '../AbstractSiteApi';
import GetFaqResponse from '../CRM/Responses/FaqResponse';
import { GoogleMap } from '../Models/GoogleMap';
import { SiteDefaultLottery } from '../Models/SiteApi/SiteDefaultLottery';
import SiteFaq from '../Models/SiteApi/SiteFaq';
import SiteFaqCategory from '../Models/SiteApi/SiteFaqCategory';

import SliderItem from '../Models/SliderItem';

import LotteryListResponse from './Response/LotteryListResponse';
import { GetSliderItems } from './Response/Sliders';
import {
    GetHeaderMenuItemsResponse,
    GetJackpotBannerDataResponse,
    GetLatestDrawResponse,
    LootboxDataResponse,
    LootboxOpenResponse,
    LootboxTicketResponse,
    PlayResponse,
    PopTrackingEventsResponse,
    UserPanelResponse,
} from './Responses';

import CasinoSliderItem from 'Api/Models/CasinoSliderItem';
import { LootboxWinEntry } from '../../components/lootbox/Types';

class SiteApi extends AbstractSiteApi {
    currentJackpot(): Promise<AxiosResponse> {
        return this.get('current-jackpot');
    }
    faq(onlyPartials: boolean = false): Promise<SiteFaq[]> {
        return this.get(onlyPartials ? 'faq?onlyPartials' : 'faq').then(
            (response: AxiosResponse<GetFaqResponse[]>): SiteFaq[] => {
                return response.data.map((faq) => new SiteFaq(faq));
            }
        );
    }

    faqCategories(): Promise<SiteFaqCategory[]> {
        return this.get('faq-categories').then((response: AxiosResponse<GetFaqResponse[]>): SiteFaqCategory[] => {
            return response.data.map((faqCategory) => new SiteFaqCategory(faqCategory));
        });
    }

    googleMapWithMarkers(mapId: number): Promise<GoogleMap> {
        return this.get(`google_map/markers/${mapId}`).then(
            (response: AxiosResponse<GoogleMap>): GoogleMap => response.data
        );
    }

    news(getAllNews: boolean, page: number = 1) {
        if (getAllNews === false) {
            return this.get('news/limited').then(function (response: AxiosResponse) {
                return {
                    data: response.data,
                    currentPage: null,
                    lastPage: null,
                };
            });
        }

        return this.get(`news?page=${page}`).then(function (response: AxiosResponse) {
            return {
                data: response.data.data,
                currentPage: response.data.current_page,
                lastPage: response.data.last_page,
            };
        });
    }

    getTermsAndConditionsUrl() {
        return this.get('terms-and-conditions-url');
    }

    getPrivacyPolicyUrl() {
        return this.get('privacy-policy-url');
    }

    lotteryList(): Promise<AxiosResponse<LotteryListResponse[]>> {
        return this.get('lottery-list');
    }

    defaultLottery(): Promise<SiteDefaultLottery> {
        return this.get('lottery/default').then(
            (response: AxiosResponse<SiteDefaultLottery>): SiteDefaultLottery => response.data
        );
    }

    getCountryListRawResponse(): Promise<AxiosResponse> {
        return this.get('country-list');
    }

    getCountryStates(country: string): Promise<AxiosResponse> {
        return this.get(`country-list/states/${country}`);
    }

    getAvailableSocialProviders(): Promise<AxiosResponse> {
        return this.get('social-providers');
    }

    checkIsUserEmailAlreadyExist(data: { email: string; phone: string }): Promise<AxiosResponse> {
        return this.post('register/check', data);
    }

    async checkIsCpfNumberValid(data: { cpf_number: string }): Promise<AxiosResponse> {
        return this.post('register/checkCpfNumber', data);
    }

    async getJackpotBannerData(displaySlug: string): Promise<GetJackpotBannerDataResponse> {
        return (await this.get('jackpot-banner-data', { params: { display_slug: displaySlug } })).data;
    }

    async getPlayData(slug: string): Promise<PlayResponse> {
        return (await this.get(`play/${slug}`)).data;
    }

    public getCountryList() {
        return this.get('countries/list').then((response) => response.data);
    }

    async getCasinoSliderItems(sliderSlug: string): Promise<Array<CasinoSliderItem>> {
        return (await this.get(`casino-sliders/${sliderSlug}`)).data;
    }

    async getLatestResultsLimited(): Promise<GetLatestDrawResponse[]> {
        return (await this.get('latest-draws-limited')).data;
    }

    async markAllNotificationsAsRead() {
        return this.get('notifications/mark-all');
    }

    async getHeaderMenuItems(): Promise<GetHeaderMenuItemsResponse[]> {
        return (await this.get('menus/header_menu_items')).data;
    }

    public async popTrackingEvents(): Promise<PopTrackingEventsResponse> {
        const params = { time: Date.now() };
        return (await this.get('account/tracking_events', { params })).data;
    }

    public async getUserForAuthPanel(): Promise<UserPanelResponse> {
        const response = await this.get('/user-panel');
        return response.data;
    }

    public getSliderItems(sliderSlug: string): Promise<SliderItem[]> {
        return this.get(`slider-items/${sliderSlug}`).then((response: AxiosResponse<GetSliderItems>) =>
            response.data.map((item) => new SliderItem(item))
        );
    }

    async getLootboxData(slug: string): Promise<LootboxDataResponse> {
        return (await this.get(`lootbox/${slug}/data`)).data;
    }

    async openLootbox(slug: string): Promise<AxiosResponse<LootboxOpenResponse>> {
        return (await this.post(`lootbox/${slug}/open`)).data;
    }

    async getLootboxLatestWins(limit: number = 20): Promise<LootboxWinEntry[]> {
        return (await this.get('lootbox/latest-wins', { params: { limit } })).data;
    }

    async getLootboxTicket(token: string): Promise<LootboxTicketResponse> {
        return (await this.get('account/lootboxes/single/', { params: { token } })).data;
    }
}

export default new SiteApi();
